import { gql } from '@apollo/client/core'

// Query to get pharmacy products
export const GET_PHARMACY_PRODUCTS = gql`
  query GetPharmacyProducts($filter: PharmacyProductFilterInput) {
    pharmacyProducts(filter: $filter) {
      data {
      id
      external_id
      gtin
      custom_product_name
      custom_label
      custom_image_url
      price
      price_in_fcfa
      formatted_price
      currency
      created_at
      updated_at
      display_name
      display_label
      display_image_url
      variant_id
      product {
        id
        name
        cis
        form
        description
      }
      variant {
        id
        label
        image_url
        product {
          id
          name
          cis
        }
      }
      stock {
        id
        quantity
        is_available
      }
      pharmacy {
        id
        name
      }
      }
      paginatorInfo {
        total
        currentPage
        lastPage
        perPage
        hasMorePages
      }
    }
  }
`

// Query to get a single pharmacy product
export const GET_PHARMACY_PRODUCT = gql`
  query GetPharmacyProduct($id: ID!) {
    pharmacyProduct(id: $id) {
      id
      external_id
      gtin
      cip_ubipharm
      cip_dpci
      cip_copharmed
      cip_tedis
      cip_datamatrix
      custom_product_name
      custom_label
      custom_image_url
      price
      price_in_fcfa
      formatted_price
      currency
      created_at
      updated_at
      display_name
      display_label
      display_image_url
      variant_id
      product {
        id
        name
        cis
        form
        description
        alternate_names
        titulaires
        prescription_conditions
        cip7_codes
        cip13_codes
      }
      variant {
        id
        label
        cip13
        cip7
        image_url
        product {
          id
          name
          cis
        }
      }
      stock {
        id
        quantity
        is_available
      }
      pharmacy {
        id
        name
      }
    }
  }
`

// Mutation to create a pharmacy product
export const CREATE_PHARMACY_PRODUCT = gql`
  mutation CreatePharmacyProduct($input: CreatePharmacyProductInput!) {
    createPharmacyProduct(input: $input) {
      id
      external_id
      gtin
      cip_ubipharm
      cip_dpci
      cip_copharmed
      cip_tedis
      cip_datamatrix
      custom_product_name
      custom_label
      custom_image_url
      price
      currency
      created_at
      updated_at
      display_name
      display_label
      display_image_url
      product {
        id
        name
        cis
      }
      variant {
        id
        label
      }
      stock {
        id
        quantity
        is_available
      }
      pharmacy {
        id
        name
      }
    }
  }
`

// Mutation to update a pharmacy product
export const UPDATE_PHARMACY_PRODUCT = gql`
  mutation UpdatePharmacyProduct($input: UpdatePharmacyProductInput!) {
    updatePharmacyProduct(input: $input) {
      id
      external_id
      gtin
      cip_ubipharm
      cip_dpci
      cip_copharmed
      cip_tedis
      cip_datamatrix
      custom_product_name
      custom_label
      custom_image_url
      price
      currency
      created_at
      updated_at
      display_name
      display_label
      display_image_url
      product {
        id
        name
        cis
      }
      variant {
        id
        label
      }
      stock {
        id
        quantity
        is_available
      }
      pharmacy {
        id
        name
      }
    }
  }
`

// Mutation to delete a pharmacy product
export const DELETE_PHARMACY_PRODUCT = gql`
  mutation DeletePharmacyProduct($id: ID!) {
    deletePharmacyProduct(id: $id) {
      id
      custom_product_name
    }
  }
`

// Mutation to map a pharmacy product to a master product
export const MAP_PHARMACY_PRODUCT = gql`
  mutation MapPharmacyProduct($input: MapPharmacyProductInput!) {
    mapPharmacyProduct(input: $input) {
      id
      custom_product_name
      custom_label
      custom_image_url
      price
      currency
      product {
        id
        name
        cis
        form
        description
      }
      variant {
        id
        label
        image_url
      }
      pharmacy {
        id
        name
      }
    }
  }
`

// Query to search master products for mapping
export const SEARCH_MASTER_PRODUCTS = gql`
  query SearchMasterProducts($query: String!, $limit: Int = 10) {
    products(search: $query, limit: $limit) {
      id
      name
      cis
      form
      description
      variants {
        id
        label
        image_url
      }
    }
  }
`

// Query to get pharmacy product by GTIN
export const GET_PHARMACY_PRODUCT_BY_GTIN = gql`
  query GetPharmacyProductByGtin($gtin: String!, $pharmacy_id: ID) {
    pharmacyProductByGtin(gtin: $gtin, pharmacy_id: $pharmacy_id) {
      id
      external_id
      gtin
      custom_product_name
      custom_label
      custom_image_url
      price
      price_in_fcfa
      formatted_price
      currency
      created_at
      updated_at
      display_name
      display_label
      display_image_url
      variant_id
      product {
        id
        name
        cis
        form
        description
      }
      variant {
        id
        label
        image_url
        product {
          id
          name
          cis
        }
      }
      stock {
        id
        quantity
        is_available
      }
      pharmacy {
        id
        name
      }
    }
  }
`

// Query to search products for mapping (used by components)
export const SEARCH_PRODUCTS_FOR_MAPPING = gql`
  query SearchProductsForMapping($input: ProductSearchInput!) {
    searchProducts(input: $input) {
      product {
        id
        name
        cis
        form
        description
        image_url
        alternate_names
        variants {
          id
          label
          cip7
          cip13
          status
          commercialization_status
          price_with_fees
          price_without_fees
          image_url
        }
      }
      pharmacyCount
    }
  }
`

// Query to get unlinked pharmacy products with matching
export const GET_UNLINKED_PHARMACY_PRODUCTS = gql`
  query GetUnlinkedPharmacyProducts($filter: UnlinkedPharmacyProductFilterInput) {
    unlinkedPharmacyProducts(filter: $filter) {
      data {
        id
        pharmacy_id
        external_id
        gtin
        cip_ubipharm
        cip_dpci
        cip_copharmed
        cip_tedis
        cip_datamatrix
        custom_product_name
        custom_label
        custom_image_url
        price
        currency
        created_at
        updated_at
        pharmacy {
          id
          name
        }
        suggested_variant {
          id
          label
          cip7
          cip13
          image_url
          product {
            id
            name
            cis
            form
            description
            image_url
          }
        }
        match_confidence
        match_type
        match_details
        has_conflict
        conflicting_product_id
        alternative_suggestions {
          id
          label
          cip7
          cip13
          image_url
          is_linked_in_pharmacy
          linked_pharmacy_product_id_in_pharmacy
          product {
            id
            name
            cis
            form
            description
            image_url
          }
        }
      }
      paginatorInfo {
        total
        currentPage
        lastPage
        perPage
        hasMorePages
      }
      stats {
        total_products
        with_matches
        without_matches
      }
    }
  }
`

// Mutation to link a pharmacy product to a variant
export const LINK_PHARMACY_PRODUCT = gql`
  mutation LinkPharmacyProduct($pharmacy_product_id: ID!, $variant_id: ID!) {
    linkPharmacyProduct(
      pharmacy_product_id: $pharmacy_product_id
      variant_id: $variant_id
    ) {
      success
      message
      code
      data
    }
  }
`

// Mutation to update the match for an unlinked pharmacy product
export const UPDATE_UNLINKED_PRODUCT_MATCH = gql`
  mutation UpdateUnlinkedProductMatch($pharmacy_product_id: ID!, $variant_id: ID!) {
    updateUnlinkedProductMatch(
      pharmacy_product_id: $pharmacy_product_id
      variant_id: $variant_id
    ) {
      success
      message
      code
      data
    }
  }
`

// Mutation to bulk link pharmacy products
export const BULK_LINK_PHARMACY_PRODUCTS = gql`
  mutation BulkLinkPharmacyProducts($pharmacy_product_ids: [ID!]!, $threshold: Int) {
    bulkLinkPharmacyProducts(
      pharmacy_product_ids: $pharmacy_product_ids
      threshold: $threshold
    ) {
      success
      message
      code
      data
    }
  }
`

// Mutation to rematch unlinked pharmacy products
export const REMATCH_UNLINKED_PHARMACY_PRODUCTS = gql`
  mutation RematchUnlinkedPharmacyProducts($pharmacy_id: ID) {
    rematchUnlinkedPharmacyProducts(pharmacy_id: $pharmacy_id) {
      success
      message
      code
      data
    }
  }
`

// Mutation to link all products with matches
export const LINK_ALL_UNLINKED_PRODUCTS_WITH_MATCHES = gql`
  mutation LinkAllUnlinkedProductsWithMatches($pharmacy_id: ID, $confidence_threshold: Int) {
    linkAllUnlinkedProductsWithMatches(pharmacy_id: $pharmacy_id, confidence_threshold: $confidence_threshold) {
      success
      message
      code
      data
    }
  }
`

// Query to get pharmacy product stock entries
export const GET_PHARMACY_PRODUCT_STOCK_ENTRIES = gql`
  query GetPharmacyProductStockEntries($pharmacy_product_id: ID!) {
    pharmacyProductStockEntries(pharmacy_product_id: $pharmacy_product_id) {
      id
      quantity
      type
      reason
      notes
      created_at
      pharmacy_product {
        id
        display_name
      }
    }
  }
`

// Mutation to manage stock for a pharmacy product
export const MANAGE_PHARMACY_PRODUCT_STOCK = gql`
  mutation ManagePharmacyProductStock($input: ManagePharmacyProductStockInput!) {
    managePharmacyProductStock(input: $input) {
      id
      quantity
      type
      reason
      notes
      created_at
      pharmacy_product {
        id
        display_name
      }
    }
  }
`

// Mutation to update stock availability (Phase 1)
export const UPDATE_PHARMACY_PRODUCT_STOCK_AVAILABILITY = gql`
  mutation UpdatePharmacyProductStockAvailability($input: UpdatePharmacyProductStockAvailabilityInput!) {
    updatePharmacyProductStockAvailability(input: $input) {
      id
      quantity
      is_available
      product {
        id
        display_name
      }
    }
  }
`

// Query to export pharmacy products to CSV
export const EXPORT_PHARMACY_PRODUCTS_CSV = gql`
  query ExportPharmacyProductsCsv($filter: PharmacyProductFilterInput) {
    exportPharmacyProductsCsv(filter: $filter) {
      success
      template_content
      filename
      language
      error
    }
  }
`

// Apollo Client functions
export const getPharmacyProducts = (variables) => {
  return window.apolloClient.query({
    query: GET_PHARMACY_PRODUCTS,
    variables
  })
}

export const getPharmacyProduct = (id) => {
  return window.apolloClient.query({
    query: GET_PHARMACY_PRODUCT,
    variables: { id }
  })
}

export const getPharmacyProductByGtin = (gtin, pharmacyId = null) => {
  return window.apolloClient.query({
    query: GET_PHARMACY_PRODUCT_BY_GTIN,
    variables: { gtin, pharmacy_id: pharmacyId }
  })
}

export const createPharmacyProduct = (input) => {
  return window.apolloClient.mutate({
    mutation: CREATE_PHARMACY_PRODUCT,
    variables: { input }
  })
}

export const updatePharmacyProduct = (input) => {
  return window.apolloClient.mutate({
    mutation: UPDATE_PHARMACY_PRODUCT,
    variables: { input }
  })
}

export const deletePharmacyProduct = (id) => {
  return window.apolloClient.mutate({
    mutation: DELETE_PHARMACY_PRODUCT,
    variables: { id }
  })
}

export const mapPharmacyProduct = (input) => {
  return window.apolloClient.mutate({
    mutation: MAP_PHARMACY_PRODUCT,
    variables: { input }
  })
}

export const searchMasterProducts = (query, limit = 10) => {
  return window.apolloClient.query({
    query: SEARCH_MASTER_PRODUCTS,
    variables: { query, limit }
  })
}

export const managePharmacyProductStock = (input) => {
  return window.apolloClient.mutate({
    mutation: MANAGE_PHARMACY_PRODUCT_STOCK,
    variables: { input }
  })
}

export const updatePharmacyProductStockAvailability = (input) => {
  return window.apolloClient.mutate({
    mutation: UPDATE_PHARMACY_PRODUCT_STOCK_AVAILABILITY,
    variables: { input }
  })
}

export const searchProductsForMapping = (query) => {
  return window.apolloClient.query({
    query: SEARCH_PRODUCTS_FOR_MAPPING,
    variables: { 
      input: { 
        query: query 
      } 
    }
  })
}

export const getUnmappedPharmacyProducts = () => {
  return window.apolloClient.query({
    query: GET_UNLINKED_PHARMACY_PRODUCTS,
    variables: {
      filter: {}
    }
  })
}

export const getPharmacyProductStockEntries = (pharmacy_product_id) => {
  return window.apolloClient.query({
    query: GET_PHARMACY_PRODUCT_STOCK_ENTRIES,
    variables: { pharmacy_product_id }
  })
}

// Upload pharmacy products via CSV
export const UPLOAD_PHARMACY_PRODUCTS_CSV_MUTATION = gql`
  mutation UploadPharmacyProductsCsv($file: Upload!, $distributor: String) {
    uploadPharmacyProductsCsv(file: $file, distributor: $distributor) {
      success
      code
      reason
      data
    }
  }
`

// Upload pharmacy product image
export const UPLOAD_PHARMACY_PRODUCT_IMAGE_MUTATION = gql`
  mutation UploadPharmacyProductImage($file: Upload!, $pharmacy_product_id: ID!) {
    uploadPharmacyProductImage(file: $file, pharmacy_product_id: $pharmacy_product_id) {
      success
      code
      reason
      data
    }
  }
`

// Query to download pharmacy products CSV template
export const DOWNLOAD_PHARMACY_PRODUCTS_CSV_TEMPLATE = gql`
  query DownloadPharmacyProductsCsvTemplate {
    downloadPharmacyProductsCsvTemplate {
      success
      template_content
      filename
      language
    }
  }
`